/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ERROR_FACTORY, ErrorCode } from '../util/errors';
import { MessagingService } from '../messaging-service';
import { updateSwReg } from '../helpers/updateSwReg';
import { updateVapidKey } from '../helpers/updateVapidKey';
import { RegisterOptions } from '../interfaces/public-types';
import { registerFcmRegistrationWithFid } from '../internals/register-fid';
import {
  dbGetFidRegistration,
  dbSetFidRegistration
} from '../internals/idb-manager';
import { notifyOnRegistered } from '../internals/token-manager';

const FID_REGISTRATION_REFRESH_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Registers the app instance with FCM using its Firebase Installation ID (FID). The FID is
 * delivered via the `onRegistered` callback. Call this to establish an FID-based identity.
 * Once `onRegistered` provides an FID, instruct your backend to remove any legacy token
 * previously associated with this instance. The backend send API supports FID as a target.
 *
 * When called multiple times, `onRegistered` is invoked on each call with the current FID.
 * Backend registration sync runs on first register, when the FID changes, or on weekly refresh.
 *
 * @param messaging - The MessagingService instance.
 * @param options - Optional. Same options as getToken (vapidKey, serviceWorkerRegistration).
 */
export async function register(
  messaging: MessagingService,
  options?: RegisterOptions
): Promise<void> {
  if (!navigator) {
    throw ERROR_FACTORY.create(ErrorCode.AVAILABLE_IN_WINDOW);
  }

  if (Notification.permission === 'default') {
    await Notification.requestPermission();
  }

  if (Notification.permission !== 'granted') {
    throw ERROR_FACTORY.create(ErrorCode.PERMISSION_BLOCKED);
  }

  if (!messaging.onRegisteredHandler) {
    throw ERROR_FACTORY.create(ErrorCode.INVALID_ON_REGISTERED_HANDLER);
  }

  await updateVapidKey(messaging, options?.vapidKey);
  await updateSwReg(messaging, options?.serviceWorkerRegistration);

  // Keep the queue alive after a failed register() so future calls can retry.
  const prev = messaging._registerNotifyChain.catch(() => {});
  messaging._registerNotifyChain = prev.then(async () => {
    const fid = await messaging.firebaseDependencies.installations.getId();

    const stored = await dbGetFidRegistration(messaging.firebaseDependencies);
    const now = Date.now();
    const shouldRefresh =
      !stored ||
      stored.fid !== fid ||
      now >= stored.lastRegisterTime + FID_REGISTRATION_REFRESH_MS;

    if (shouldRefresh) {
      await registerFcmRegistrationWithFid(messaging, fid);
      await dbSetFidRegistration(messaging.firebaseDependencies, {
        fid,
        lastRegisterTime: now,
        vapidKey: messaging.vapidKey
      });
    }

    const handler = messaging.onRegisteredHandler;
    if (!handler) {
      throw ERROR_FACTORY.create(ErrorCode.INVALID_ON_REGISTERED_HANDLER);
    }

    notifyOnRegistered(messaging, fid);
  });
  return messaging._registerNotifyChain;
}
